import React, { useState } from 'react';
import { DeleteIcon, EditIcon, EyeIcon } from '../../components/Shared/TableActionIcons';
import { useDispatch } from 'react-redux';
import CustomPagination from '../../components/Shared/CustomPagination';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../Transactions/TransactionsTable';
import TableLoading from '../../components/Shared/TableLoading';
import CustomTable from '../../components/Shared/CustomTable';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/Shared/ConfirmationModal';
import { FiTrash2 } from 'react-icons/fi';
import { deletePayrun } from '../../store/slices/PayslipSlice';

export default function PayrunTable({ data, loading, onPageChange, onRefresh }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedData, setSelectedData] = useState(null);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const handlePageChange = (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  };

  const confirmDeleteHandler = async () => {
    try {
      setDelLoading(true);
      if (selectedData) {
        const resultAction = await dispatch(deletePayrun({ id: selectedData?.id }));
        if (deletePayrun.fulfilled.match(resultAction)) {
          if (onRefresh) onRefresh();
          setConfirmationDialogVisible(false);
          setSelectedData(null);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDelLoading(false);
    }
  };

  const templateBody = rowData => (
    <div className='flex gap-1'>
      <EyeIcon
        onClick={() => navigate('/manager/payrun/view', { state: { payrun: rowData } })}
        title={t('view')}
      />
      <EditIcon
        onClick={() => navigate('/manager/payrun/create', { state: { editingPayrun: rowData } })}
        title={t('edit')}
      />
      <DeleteIcon
        onClick={() => {
          setSelectedData(rowData);
          setConfirmationDialogVisible(true);
        }}
        title={t('delete')}
      />
    </div>
  );

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (data?.page - 1) * data?.per_page;
  };

  const basic_salary_body = rowData => {
    return (
      <>
        {rowData?.basic_salary} {t('SAR')}
      </>
    );
  };
  const allowances_body = rowData => {
    return (
      <>
        {rowData?.allowances} {t('SAR')}
      </>
    );
  };
  const deductions_body = rowData => {
    return (
      <>
        {rowData?.deductions} {t('SAR')}
      </>
    );
  };
  const net_salary_body = rowData => {
    return (
      <>
        {rowData?.net_salary} {t('SAR')}
      </>
    );
  };
  const gosi_contribution_body = rowData => {
    return (
      <>
        {rowData?.gosi_contribution} {t('SAR')}
      </>
    );
  };

  const columnsData = [
    {
      body: templateBody,
      header: t('actions')
    },
    {
      body: sequenceBodyTemplate,
      header: t('no'),
      style: { width: '3rem' }
    },
    {
      field: 'payment_date',
      body: rowData => formatDate(rowData.payment_date),
      header: t('payment_date')
    },
    {
      field: 'pay_period',
      header: t('pay_period')
    },
    {
      field: 'full_name',
      header: t('name'),
      body: rowData => (
        <div>
          {rowData.employee.first_name} {rowData.employee.last_name}
        </div>
      )
    },
    {
      field: 'basic_salary',
      body: basic_salary_body,
      header: t('basic_salary')
    },
    {
      field: 'allowances',
      body: allowances_body,
      header: t('allowances')
    },
    {
      field: 'deductions',
      body: deductions_body,
      header: t('deductions')
    },
    {
      field: 'net_salary',
      body: net_salary_body,
      header: t('net_salary')
    },
    {
      field: 'gosi_contribution',
      body: gosi_contribution_body,
      header: t('gosi_contribution')
    },

  ];

  return (
    <>
      <CustomTable
        data={data?.data}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={data?.per_page}
      />
      <ConfirmationModal
        message={`${t('do_you_really_want_to_delete_this_payrun')}?`}
        header={t('delete_confirmation')}
        Icon={FiTrash2}
        visible={confirmationDialogVisible}
        setVisible={setConfirmationDialogVisible}
        isLoading={delLoading}
        onAccept={confirmDeleteHandler}
        acceptLabel={t('delete')}
        rejectLabel={t('cancel')}
      />
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page}
          rowsPerPage={data?.per_page}
          totalRecords={data?.total_pages}
          totalRecord={data?.total_record}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
}
